<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import { Button, Select, Label, Input } from "flowbite-svelte";
  import Icon from "@iconify/svelte";
  import SortableContainer from "$lib/admin/components/common/SortableContainer.svelte";
  import TipTapEditor from "$lib/admin/components/content/TipTapEditor/TipTapEditor.svelte";

  /**
   * This component is more complex due to several requirements:
   *
   * 1. Legacy Content Support: The component must handle both old format (string items) and
   *    new format (objects with id and text properties) to maintain backward compatibility.
   *
   * 2. Content Normalization: All list items are normalized to ensure consistent structure
   *    with proper paragraph tags and unique IDs for sortability.
   *
   * 3. Property Migration: We're transitioning from using 'listStyle' to 'listIcon' for
   *    icon-based lists. When editing, we remove 'listStyle' and use only 'listIcon' to
   *    gradually phase out the legacy property while maintaining compatibility.
   *
   * 4. HTML Content Preservation: List items can contain rich HTML content that must be
   *    preserved during editing, sorting, and saving operations.
   *
   * 5. Consistent Paragraph Structure: We need to ensure all content is wrapped in a
   *    paragraph but also avoid double-wrapping content that already has paragraph tags.
   */

  const dispatch = createEventDispatcher();

  export let props: any = null;

  if (!props.listItems) props.listItems = [];
  if (!props.listType) props.listType = "ul";

  const listTypes = [
    { value: "bullet", label: "Bullet List" },
    { value: "ordered", label: "Ordered List" },
    { value: "icon", label: "Icon List" },
  ];

  const listIcons = [
    { value: "pros", label: "Pros" },
    { value: "cons", label: "Cons" },
    { value: "fire", label: "Fire" },
    { value: "gift", label: "Gift" },
  ];

  let listItemsNormalized = false;

  /**
   * On component mount:
   * 1. Convert legacy list types (ul/ol) to new format (bullet/ordered/icon)
   * 2. Migrate from listStyle to listIcon for icon lists
   * 3. Normalize list items to ensure consistent structure
   */
  onMount(() => {
    if (!props.listType) {
      props.listType = "bullet";
    }

    if (props.listType === "ul" && !props.listStyle) {
      props.listType = "bullet";
    } else if (props.listType === "ul" && props.listStyle) {
      props.listType = "icon";
      props.listIcon = props.listStyle;
    } else if (props.listType === "ol") {
      props.listType = "ordered";
    }

    if (props.listType === "icon") {
      if (props.listStyle && !props.listIcon) {
        props.listIcon = props.listStyle;
      } else if (!props.listStyle && !props.listIcon) {
        props.listIcon = "fire";
      }
    }

    if (!listItemsNormalized) {
      normalizeListItems();
      listItemsNormalized = true;
    }
  });

  function hasParaTags(content: string): boolean {
    return content.trim().startsWith("<p>") && content.trim().endsWith("</p>");
  }

  function ensureParaTags(content: string): string {
    if (!content || content.trim() === "") return "<p></p>";
    if (hasParaTags(content)) return content;
    return `<p>${content}</p>`;
  }

  /**
   * Normalizes list items to ensure consistent structure
   * - Converts string items to objects with id and text properties
   * - Ensures all items have unique IDs for sortability
   * - Ensures all text content has proper paragraph tags
   * - Handles empty or missing text properties
   */
  function normalizeListItems() {
    if (!props.listItems) {
      props.listItems = [];
      return;
    }

    props.listItems = props.listItems.map((item: any, index: number) => {
      const uniqueId = `item-${index}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      if (typeof item === "string") {
        return {
          id: uniqueId,
          text: ensureParaTags(item),
        };
      } else if (typeof item === "object") {
        if (!item.id) {
          item = { ...item, id: uniqueId };
        }

        if (typeof item.text === "string") {
          if (item.text !== "") {
            item.text = ensureParaTags(item.text);
          } else {
            item.text = "<p></p>";
          }
        } else if (!item.hasOwnProperty("text")) {
          item.text = "<p></p>";
        }

        return item;
      }

      return item;
    });
  }

  function handleListTypeChange() {
    if (props.listType === "ordered") {
      props.htmlElement = "ol";
      props.listIcon = "";
    } else if (props.listType === "bullet") {
      props.htmlElement = "ul";
      props.listIcon = "";
    } else if (props.listType === "icon") {
      props.htmlElement = "ul";
      if (!props.listIcon) {
        props.listIcon = "fire";
      }
    }

    if (props.listStyle !== undefined) {
      delete props.listStyle;
    }

    dispatch("keyup");
  }

  function handleIconChange() {
    if (!props.listIcon) {
      props.listIcon = "fire";
    }

    if (props.listStyle !== undefined) {
      delete props.listStyle;
    }

    dispatch("keyup");
  }

  function addListItem() {
    if (!Array.isArray(props.listItems)) props.listItems = [];
    const newItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      text: "",
    };

    props.listItems = [...props.listItems, newItem];
    normalizeListItems();

    if (props.listStyle !== undefined) {
      delete props.listStyle;
    }

    dispatch("keyup");
  }

  function removeListItem(index: number) {
    props.listItems.splice(index, 1);
    normalizeListItems();

    if (props.listStyle !== undefined) {
      delete props.listStyle;
    }

    dispatch("keyup");
  }

  function handleSortableUpdate(event: CustomEvent) {
    const { items } = event.detail as { items: any[] };
    const originalItems = [...props.listItems];

    const originalItemsMap = new Map<string, any>();
    originalItems.forEach((item: any) => {
      if (typeof item === "object" && item.id) {
        originalItemsMap.set(item.id, item);
      }
    });

    const updatedItems = items.map((item: any) => {
      if (typeof item === "object" && item.id && originalItemsMap.has(item.id)) {
        return originalItemsMap.get(item.id);
      }
      return item;
    });

    props.listItems = updatedItems;
    normalizeListItems();

    if (props.listStyle !== undefined) {
      delete props.listStyle;
    }

    dispatch("keyup");
  }
  $: normalizeListItems();
</script>

<div class="space-y-4">
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <Label for="list-type">List type</Label>
      <Select id="list-type" bind:value={props.listType} on:change={handleListTypeChange}>
        {#each listTypes as type}
          <option value={type.value}>{type.label}</option>
        {/each}
      </Select>
    </div>

    {#if props.listType === "icon"}
      <div>
        <Label for="list-icon">List Icon</Label>
        <Select id="list-icon" bind:value={props.listIcon} on:change={handleIconChange}>
          {#each listIcons as icon}
            <option value={icon.value}>{icon.label}</option>
          {/each}
        </Select>
      </div>
    {/if}
  </div>

  {#if props.listItems && props.listItems.length > 0}
    <div class="mt-4">
      <SortableContainer
        items={props.listItems}
        containerClass="space-y-2"
        handleClass="drag-handle"
        on:update={handleSortableUpdate}
        on:keyup={() => dispatch("keyup")}
        autoNotify={true}
        generateId={true}
        idField="id">
        {#each props.listItems as item, index (item.id || index)}
          <div class="flex items-center gap-2 p-2 border border-gray-200 rounded-lg bg-white" data-id={item.id}>
            <div class="p-2 cursor-move drag-handle">
              <Icon icon="heroicons:bars-3" class="w-4 h-4 text-gray-500" />
            </div>
            <div class="flex-grow">
              {#if typeof item === "string"}
                <!-- Fall back to regular input for string items during SSR -->
                <Input type="text" bind:value={props.listItems[index]} on:keyup={() => dispatch("keyup")} />
              {:else if item && typeof item === "object" && "text" in item}
                <TipTapEditor bind:text={item.text} on:keyup={() => dispatch("keyup")} compactMode={true} />
              {:else}
                <!-- Fallback if the item is in an unexpected format -->
                <Input type="text" value="Invalid item format" disabled />
              {/if}
            </div>
            <Button color="red" class="rounded-sm" size="sm" on:click={() => removeListItem(index)}>
              <Icon icon="heroicons:trash" class="w-4 h-4" />
            </Button>
          </div>
        {/each}
      </SortableContainer>
    </div>
  {/if}

  <Button color="blue" class="mt-4" on:click={() => addListItem()}>
    <Icon icon="heroicons:plus" class="mr-2 w-4 h-4" />
    Add item
  </Button>
</div>
